import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { inject, NgModule } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, Router, RouterModule, Routes } from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';

import { ConfigService } from './config.service';
import { AdminGuard } from './guards/admin.guard';
import { AuthGuard } from './guards/auth.guard';
import { ChangeManagerGuard } from './guards/change-manager.guard';
import { projectAccessGuard } from './guards/project-access.guard';
import { verifyUserGuard } from './guards/verifyUserGuard';
import { LoginComponent } from './login/login.component';
import { PermissionService } from './permission.service';
import { ProjectIndexComponent } from './projects/project-index/project-index.component';
import { ProjectDescriptionTabComponent } from './projects/project-manage/project-description-tab/project-description-tab.component';
import { ProjectNoAccessComponent } from './projects/project-no-access/project-no-access.component';
import { SearchResultsComponent } from './search/search-results/search-results.component';
import { Error404Component } from './shared/components/error-404/error-404.component';
import { PermissionAction } from './shared/enums/permission-action.enum';

const isSessionTemplateV2Enabled = (): boolean => inject(ConfigService).isFeatureEnabled('session_template_v2');

const tasksRouteGuard = (route: ActivatedRouteSnapshot): Observable<boolean> => {
  const config = inject(ConfigService);
  const router = inject(Router);
  const permissionService = inject(PermissionService);
  const tasksRefactorEnabled = config.isFeatureEnabled('tasks_refactor');
  const readerTasksEnabled = config.isFeatureEnabled('reader_tasks');
  if (!tasksRefactorEnabled) {
    if (readerTasksEnabled) {
      router.navigateByUrl('/legacy-tasks');
    }
    return of(false);
  }

  // find the current projectId in the route tree
  let currentRoute = route;
  let projectId: string | null = null;
  while (currentRoute) {
    if (!projectId) {
      projectId = currentRoute.paramMap.get('projectId');
    }
    currentRoute = currentRoute.parent;
  }

  if (projectId) {
    return toObservable(permissionService.check(projectId, [PermissionAction.TasksReportRead])).pipe(
      switchMap((hasAccess) => {
        if (!hasAccess) {
          router.navigate(['projects', projectId, 'no-access']);
        }
        return of(hasAccess);
      }),
    ) as Observable<boolean>;
  }
  return of(true);
};

const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: '',
    pathMatch: 'prefix',
    canActivate: [AuthGuard, verifyUserGuard],
    canActivateChild: [AuthGuard, verifyUserGuard],
    // all routes in here are auth-guarded
    children: [
      {
        path: '',
        redirectTo: 'projects',
        pathMatch: 'full',
      },
      {
        path: 'logout',
        canActivate: [],
        component: LoginComponent,
      },
      {
        path: 'projects',
        component: ProjectIndexComponent,
        pathMatch: 'prefix',
      },
      {
        path: 'projects/:projectId',
        pathMatch: 'full',
        redirectTo: 'projects/:projectId/description',
      },
      {
        path: 'projects/:projectId/no-access',
        pathMatch: 'full',
        component: ProjectNoAccessComponent,
      },
      {
        path: 'projects/:projectId',
        // doing as a canMatch because they re-run on same navigation
        // that allows us to rerun this guard when exhaustiveMode is toggled
        canMatch: [projectAccessGuard],
        loadComponent: () => import('./projects/project-manage/project-manage.component').then((m) => m.ProjectManageComponent),
        canDeactivate: [],
        pathMatch: 'prefix',
        children: [
          {
            path: 'description',
            canDeactivate: [ChangeManagerGuard],
            component: ProjectDescriptionTabComponent,
          },
          {
            path: 'analyses',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./jobs/analysis-list/analysis-list.component').then((m) => m.AnalysisListComponent),
          },
          {
            path: 'info',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () =>
              import('./projects/project-manage/project-info-tab/project-info-tab.component').then((m) => m.ProjectInfoTabComponent),
          },
          {
            path: 'sessions',
            canDeactivate: [ChangeManagerGuard],
            redirectTo: 'sessions/',
          },
          {
            path: 'sessions/:entityId',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./sessions/session-list/session-list.component').then((m) => m.SessionListComponent),
          },
          {
            path: 'subjects',
            canDeactivate: [ChangeManagerGuard],
            redirectTo: 'subjects/',
          },
          {
            path: 'subjects/:entityId',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./sessions/subject-list/subject-list.component').then((m) => m.SubjectListComponent),
          },
          {
            path: 'dataviews',
            loadChildren: () => import('./data-views/data-views.module').then((m) => m.DataViewsModule),
          },
          {
            path: 'workspaces',
            loadChildren: () => import('./workspaces/workspaces.module').then((m) => m.WorkspacesModule),
          },
          {
            path: 'tasks',
            redirectTo: 'legacy-tasks',
          },
          {
            path: 'legacy-tasks',
            canDeactivate: [ChangeManagerGuard],
            loadChildren: () => import('./deprecated-tasks/tasks.module').then((m) => m.TasksModule),
          },
          {
            path: 'templates',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () =>
              isSessionTemplateV2Enabled()
                ? import('./projects/project-session-templates/project-session-templates.component').then(
                    (m) => m.ProjectSessionTemplatesComponent,
                  )
                : import('./projects/legacy-project-session-templates/legacy-project-session-templates.component').then(
                    (m) => m.LegacyProjectSessionTemplatesComponent,
                  ),
          },
          {
            // TODO: remove this route after Templates v2 development is done
            path: 'templates-v2',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () =>
              import('./projects/project-session-templates/project-session-templates.component').then(
                (m) => m.ProjectSessionTemplatesComponent,
              ),
          },
          {
            path: 'gear-rules',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () =>
              import('./projects/project-gear-rules/project-gear-rules.component').then((m) => m.ProjectGearRulesComponent),
          },
          {
            path: 'settings',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./projects/project-settings/project-settings.component').then((m) => m.ProjectSettingsComponent),
          },
          {
            path: 'settings/:settingsTab',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./projects/project-settings/project-settings.component').then((m) => m.ProjectSettingsComponent),
          },
          {
            path: 'imports',
            children: [
              {
                path: '',
                canDeactivate: [ChangeManagerGuard],
                loadComponent: () => import('./project-imports/project-imports.component').then((m) => m.ProjectImportsComponent),
                pathMatch: 'full',
              },
              {
                path: ':importId',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./project-imports/import-details/project-import-details.component').then(
                        (m) => m.ProjectImportDetailsComponent,
                      ),
                  },
                  {
                    path: 'conflicts',
                    loadComponent: () =>
                      import('./project-imports/conflict-files/conflict-files.component').then((m) => m.ConflictFilesComponent),
                  },
                ],
              },
            ],
          },
          {
            path: 'exports',
            children: [
              {
                path: '',
                canDeactivate: [ChangeManagerGuard],
                loadComponent: () => import('./project-exports/project-exports.component').then((m) => m.ProjectExportsComponent),
                pathMatch: 'full',
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./project-exports/export-details/project-export-details.component').then((m) => m.ProjectExportDetailsComponent),
              },
            ],
          },
          {
            path: 'applications',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () =>
              import('./projects/project-applications/project-applications.component').then((m) => m.ProjectApplicationsComponent),
          },
          {
            path: 'auditTrailReports',
            data: { requiredPermission: PermissionAction.AuditTrailReportsView },
            canActivate: [AdminGuard],
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () =>
              import('./projects/project-audit-reports/project-audit-reports.component').then((m) => m.ProjectAuditReportsComponent),
          },
          {
            path: 'permissions',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () =>
              import('./projects/project-manage/project-permissions-tab/project-permissions-tab.component').then(
                (m) => m.ProjectPermissionsTabComponent,
              ),
          },
          {
            path: 'task-report-queue',
            canActivate: [tasksRouteGuard],
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./task-reports-queue/task-reports-queue.component').then((m) => m.TaskReportsQueueComponent),
          },
          /***Legacy redirects****/
          {
            path: 'gearRules',
            redirectTo: 'gear-rules',
          },
          {
            path: 'information',
            redirectTo: 'info',
          },
          {
            path: 'sessionTemplate',
            redirectTo: 'templates',
          },
          {
            path: 'dataViews',
            redirectTo: 'dataviews',
          },
          /**********************/
        ],
      },
      {
        path: 'sessions',
        canDeactivate: [ChangeManagerGuard],
        redirectTo: 'sessions/',
      },
      {
        path: 'sessions/:entityId',
        canDeactivate: [ChangeManagerGuard],
        loadComponent: () => import('./sessions/sessions-page.component').then((m) => m.SessionsPageComponent),
      },
      {
        path: 'subjects',
        canDeactivate: [ChangeManagerGuard],
        redirectTo: 'subjects/',
      },
      {
        path: 'subjects/:entityId',
        canDeactivate: [ChangeManagerGuard],
        loadComponent: () => import('./sessions/subjects-page.component').then((m) => m.SubjectsPageComponent),
      },
      {
        path: 'collections',
        loadChildren: () => import('./collections/collection.module').then((m) => m.CollectionModule),
      },
      {
        path: 'tasks',
        canActivate: [tasksRouteGuard],
        children: [
          {
            path: '',
            loadComponent: () => import('./task-manager/task-manager.component').then((m) => m.TaskManagerComponent),
          },
          {
            path: 'reader',
            redirectTo: 'reader/',
          },
          {
            path: 'reader/:taskId',
            loadComponent: () => import('./task-manager/task-manager.component').then((m) => m.TaskManagerComponent),
          },
        ],
      },
      {
        path: 'legacy-tasks',
        loadChildren: () => import('./deprecated-tasks/tasks.module').then((m) => m.TasksModule),
      },
      {
        path: 'dicom-uploader',
        loadComponent: () => import('./upload/dicom-uploader/dicom-uploader.component').then((m) => m.DicomUploaderComponent),
      },
      {
        path: 'add-data',
        loadComponent: () => import('./add-data/add-data.component').then((m) => m.AddDataComponent),
      },
      {
        // stops a remount from happening
        // treat no :jobId as the same route just with '' as the jobId
        path: 'jobs',
        redirectTo: 'jobs/',
      },
      {
        path: 'jobs/:jobId',
        loadComponent: () => import('./jobs/jobs.component').then((m) => m.JobsComponent),
      },
      {
        path: 'gears',
        loadComponent: () => import('./gears/installed-gears/installed-gears.component').then((m) => m.InstalledGearsComponent),
      },
      {
        path: 'report',
        loadComponent: () => import('./reporting/project-report/project-report.component').then((m) => m.ProjectReportComponent),
      },
      {
        path: 'usage-legacy',
        canActivate: [AdminGuard],
        loadComponent: () =>
          import('./reporting/usage-report-legacy/usage-report-legacy.component').then((m) => m.UsageReportLegacyComponent),
      },
      {
        path: 'usage',
        canActivate: [AdminGuard],
        loadComponent: () => import('./reporting/usage-report/usage-report.component').then((m) => m.UsageReportComponent),
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full',
          },
          {
            path: 'dashboard',
            loadComponent: () =>
              import('./reporting/usage-report/usage-report-dashboard/usage-report-dashboard.component').then(
                (m) => m.UsageReportDashboardComponent,
              ),
          },
          {
            path: 'report',
            loadComponent: () => import('./reporting/usage-report/usage-table/usage-table.component').then((m) => m.UsageTableComponent),
          },
        ],
      },
      {
        path: 'admin',
        children: [
          {
            path: 'users',
            canActivate: [AdminGuard],
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./admin/users/manage-users/manage-users.component').then((m) => m.ManageUsersComponent),
          },
          {
            path: 'groups',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./admin/groups/group-manage/group-manage.component').then((m) => m.GroupManageComponent),
          },
          {
            path: 'groups/:groupId',
            redirectTo: 'groups/:groupId/projects',
          },
          {
            path: 'groups/:groupId/:groupTab',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./admin/groups/group-manage/group-manage.component').then((m) => m.GroupManageComponent),
          },
          {
            path: 'roles',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./admin/roles/roles.component').then((m) => m.RolesComponent),
          },
          {
            path: 'roles/:entityId',
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./admin/roles/roles.component').then((m) => m.RolesComponent),
          },
          {
            path: 'staffing-pools',
            canActivate: [AdminGuard],
            canDeactivate: [ChangeManagerGuard],
            loadComponent: () => import('./admin/staffing-pools/staffing-pools.component').then((m) => m.StaffingPoolsComponent),
          },
          {
            path: 'applications',
            canActivate: [AdminGuard],
            loadComponent: () => import('./admin/applications/app-registration.component').then((m) => m.AppRegistrationComponent),
          },
          {
            path: 'interfaces',
            canActivate: [AdminGuard],
            loadComponent: () => import('./admin/devices/device-overview.component').then((m) => m.DeviceOverviewComponent),
            children: [
              {
                path: '',
                redirectTo: 'settings',
                pathMatch: 'full',
              },
              {
                path: 'settings',
                loadComponent: () => import('./admin/devices/device-settings.component').then((m) => m.DeviceSettingsComponent),
              },
              {
                path: 'connector',
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    loadComponent: () => import('./admin/cloud-connector/cloud-connector.component').then((m) => m.CloudConnectorComponent),
                  },
                  {
                    path: 'manage-connections',
                    loadComponent: () =>
                      import('./admin/cloud-connector/manage-connections/manage-connections.component').then(
                        (m) => m.ManageConnectionsComponent,
                      ),
                  },
                ],
              },
              {
                path: 'dicom-peers',
                loadComponent: () => import('./admin/devices/dicom-peers.component').then((m) => m.DicomPeersComponent),
              },
              {
                path: 'external-storages',
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    loadComponent: () =>
                      import('./admin/external-storage/external-storage.component').then((m) => m.ExternalStorageComponent),
                  },
                  {
                    path: ':id',
                    loadComponent: () =>
                      import('./admin/external-storage/details/external-storage-details.component').then(
                        (m) => m.ExternalStorageDetailsComponent,
                      ),
                  },
                ],
              },
            ],
          },
          {
            path: 'dataviews',
            canActivate: [AdminGuard],
            loadChildren: () => import('./data-views/data-views.module').then((m) => m.DataViewsModule),
          },
        ],
      },
      {
        path: 'accesslog',
        canActivate: [AdminGuard],
        loadComponent: () => import('./reporting/access-log/access-log.component').then((m) => m.AccessLogComponent),
      },
      {
        path: 'rules',
        canActivate: [AdminGuard],
        loadComponent: () => import('./gears/gear-rule-templates/gear-rule-templates.component').then((m) => m.GearRuleTemplatesComponent),
      },
      {
        path: 'settings',
        canActivate: [AdminGuard],
        loadComponent: () => import('./admin/site-settings/site-settings.component').then((m) => m.SiteSettingsComponent),
      },
      {
        path: 'task-manager/reader',
        loadComponent: () => import('./task-manager/task-manager.component').then((m) => m.TaskManagerComponent),
      },
      {
        path: 'task-manager',
        loadComponent: () => import('./task-manager/task-manager.component').then((m) => m.TaskManagerComponent),
      },
      {
        path: 'search',
        loadComponent: () => import('./search/search-results/search-results.component').then((m) => m.SearchResultsComponent),
      },
      {
        path: 'search/advanced',
        loadComponent: () => import('./search/advanced-query/advanced-query.component').then((m) => m.AdvancedQueryComponent),
      },

      {
        path: 'search/:searchTab',
        component: SearchResultsComponent,
      },
      {
        path: 'profile',
        loadComponent: () => import('./profile/profile.component').then((m) => m.ProfileComponent),
      },
    ],
  },
  // 404 catcher, put all routes before this
  {
    path: '**',
    component: Error404Component,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: true,
      enableTracing: false, // enable for router debugging
    }),
  ],
  exports: [RouterModule],
  providers: [{ provide: LocationStrategy, useClass: HashLocationStrategy }],
})
export class AppRoutingModule {}
